import { DeleteFilled, PlusOutlined } from "@ant-design/icons";
import { Typography, Switch, message, Select } from "antd";
import { useState, useEffect } from "react";

const { Title } = Typography;

const SelectProducts = ({
  orderItems,
  handleProductSelect,
  productsLoading,
  productDropdownOptions,
  handleQuantityChange,
  getEquivalent,
  removeOrderItem,
  addOrderItem,
  calculateTotal,
  discount,
  setDiscount,
  discountType,
  setDiscountType,
}) => {
  // Local state to track input values for immediate display
  const [localQuantities, setLocalQuantities] = useState({});

  // Sync local quantities with orderItems when they change
  useEffect(() => {
    const newLocalQuantities = {};
    orderItems.forEach((item) => {
      if (localQuantities[item.id] === undefined) {
        newLocalQuantities[item.id] =
          item.quantity > 0 ? item.quantity.toString() : "";
      } else {
        newLocalQuantities[item.id] = localQuantities[item.id];
      }
    });
    setLocalQuantities(newLocalQuantities);
  }, [orderItems.length]);

  const handleInputChange = (itemId, value) => {
    // Allow only numbers or empty string
    if (value === "" || /^\d+$/.test(value)) {
      setLocalQuantities((prev) => ({
        ...prev,
        [itemId]: value,
      }));
    }
  };

  const handleInputBlur = (itemId) => {
    const item = orderItems.find((i) => i.id === itemId);
    if (!item || !item.productId) return;

    const inputValue = localQuantities[itemId];
    const minQty = 1;
    const maxQty = item.currentQuantity || 0;

    // Handle empty input
    if (inputValue === "" || inputValue === "0") {
      message.warning(
        `Quantity cannot be empty or zero. Setting to minimum (${minQty})`,
        2
      );
      setLocalQuantities((prev) => ({
        ...prev,
        [itemId]: minQty.toString(),
      }));
      handleQuantityChange(itemId, minQty.toString());
      return;
    }

    const parsedQty = parseInt(inputValue, 10);

    // Validate against minimum
    if (parsedQty < minQty) {
      message.warning(
        `Quantity cannot be less than ${minQty} for ${item.productName}. Setting to minimum.`,
        2
      );
      setLocalQuantities((prev) => ({
        ...prev,
        [itemId]: minQty.toString(),
      }));
      handleQuantityChange(itemId, minQty.toString());
      return;
    }

    // Validate against maximum (current quantity)
    if (parsedQty > maxQty) {
      message.warning(
        `Only ${maxQty} units available for ${item.productName}. Setting to maximum.`,
        2
      );
      setLocalQuantities((prev) => ({
        ...prev,
        [itemId]: maxQty.toString(),
      }));
      handleQuantityChange(itemId, maxQty.toString());
      return;
    }

    // Valid quantity - update parent state
    handleQuantityChange(itemId, parsedQty.toString());
  };

  return (
    <div>
      <div className="border-1 border-[#E8E8E8] rounded">
        <Title level={5} className="text-lg font-medium p-3">
          Choose products and quantity
        </Title>

        <div className="overflow-hidden">
          <div className="grid grid-cols-12 gap-3 p-3 bg-[#EEF0F4] border-1 border-[#E8E8E8] text-xs font-medium text-gray-700">
            <div className="col-span-3">PRODUCT</div>
            <div className="col-span-2 text-center">QUANTITY</div>
            <div className="col-span-2 text-center">EQUIVALENT</div>
            <div className="col-span-2 text-center">PRICE</div>
            <div className="col-span-2 text-center">SUBTOTAL</div>
            <div className="col-span-1"></div>
          </div>

          {orderItems.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No products added yet. Click "Add Product" to start.
            </div>
          ) : (
            <div>
              {orderItems.map((item) => {
                const isOutOfStock =
                  item.productId && item.currentQuantity === 0;

                return (
                  <div
                    key={item.id}
                    className="grid grid-cols-12 gap-4 p-3 border-b items-center"
                  >
                    <div className="col-span-3">
                      <Select
                        className="w-full"
                        placeholder={
                          productsLoading ? "Loading..." : "--Choose Product--"
                        }
                        value={item.productId || undefined}
                        onChange={(value) =>
                          handleProductSelect(item.id, value)
                        }
                        options={productDropdownOptions}
                        loading={productsLoading}
                        disabled={productsLoading}
                        showSearch
                        filterOption={(input, option) =>
                          (option?.label ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      />
                      {isOutOfStock && (
                        <div className="text-xs text-red-500 mt-1 font-medium">
                          ⚠ Out of stock
                        </div>
                      )}
                    </div>

                    {/* Quantity */}
                    <div className="col-span-2">
                      <input
                        type="text"
                        pattern="[0-9]*"
                        inputMode="numeric"
                        className={`w-full p-2 border rounded text-center text-sm ${
                          isOutOfStock
                            ? "border-red-300 bg-red-50 cursor-not-allowed text-gray-400"
                            : "border-gray-300"
                        }`}
                        value={localQuantities[item.id] || ""}
                        onChange={(e) =>
                          handleInputChange(item.id, e.target.value)
                        }
                        onBlur={() => handleInputBlur(item.id)}
                        disabled={!item.productId || isOutOfStock}
                        placeholder={isOutOfStock ? "N/A" : "0"}
                      />
                    </div>

                    {/* Equivalent */}
                    <div className="col-span-2 text-center text-sm text-gray-600">
                      {item.productId && !isOutOfStock
                        ? getEquivalent(item)
                        : "-"}
                    </div>

                    {/* Price */}
                    <div className="col-span-2 text-center text-sm">
                      ₦{item.price.toLocaleString()}
                    </div>

                    {/* Subtotal */}
                    <div className="col-span-2 text-center text-sm font-medium">
                      ₦{item.subtotal.toLocaleString()}
                    </div>

                    {/* Delete */}
                    <div className="col-span-1 text-center">
                      <button
                        onClick={() => removeOrderItem(item.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <DeleteFilled size={18} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <button
          onClick={addOrderItem}
          className="my-4 mr-3 px-4 py-2 border border-orange-500 text-orange-500 rounded hover:bg-orange-50 text-sm flex items-center gap-2 ml-auto"
          disabled={productsLoading}
        >
          <PlusOutlined size={16} /> Add Product
        </button>
      </div>

      <div className="mt-6 text-right">
        <span className="text-sm text-gray-600">TOTAL AMOUNT: </span>
        <span className="text-lg font-bold">
          ₦{calculateTotal().toLocaleString()}
        </span>
      </div>

      <div className="mt-6 flex items-center gap-3">
        <span className="text-sm font-medium">Discount Type:</span>
        <Switch
          checked={discountType === "percentage"}
          onChange={(checked) =>
            setDiscountType(checked ? "percentage" : "fixed")
          }
          checkedChildren="(%)"
          unCheckedChildren="(₦)"
        />
      </div>

      <div className="mt-6">
        <h4 className="text-sm font-medium mb-2">Apply Discount/Promos</h4>
        <input
          type="text"
          placeholder="input discount value"
          className="w-full p-3 border border-gray-300 rounded text-sm"
          value={discount}
          onChange={(e) => setDiscount(e.target.value)}
        />
      </div>
    </div>
  );
};

export default SelectProducts;
